/**
 * Applies the user's theme and reduced-motion preferences to the document.
 *
 * The tokens live in tokens.css keyed off `data-theme`; nothing set that
 * attribute before, so the app was always light. "system" follows the OS.
 */

export type ThemePreference = 'system' | 'light' | 'dark';

export function applyAppearance(theme: ThemePreference, reducedMotion: boolean): void {
  const root = document.documentElement;

  const resolved =
    theme === 'system'
      ? window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light'
      : theme;

  root.setAttribute('data-theme', resolved);
  // The CSS in globals.css keys off the literal value 'true'.
  if (reducedMotion) root.setAttribute('data-reduced-motion', 'true');
  else root.removeAttribute('data-reduced-motion');
}

let systemListener: ((e: MediaQueryListEvent) => void) | null = null;

/** Keeps "system" live: re-applies when the OS flips between light and dark. */
export function watchSystemTheme(getTheme: () => ThemePreference, reducedMotion: () => boolean): void {
  const media = window.matchMedia('(prefers-color-scheme: dark)');
  if (systemListener) media.removeEventListener('change', systemListener);

  systemListener = () => {
    if (getTheme() === 'system') applyAppearance('system', reducedMotion());
  };
  media.addEventListener('change', systemListener);
}
